import moment from 'moment';
import { TestRoomStatusEnum } from 'src/app/core/enums';
import { ReportTemplate } from '../../working-area/components/working-area2/types/signal-types';
export const TestIndexFields = {
  'id': 'Test ID',
  'name': 'Test Name',
  'patientId': 'Patient Id',
  'medicalNumber': 'Medical Number',
  'deviceId': 'HW Model',
  'serial': 'HW SN',
  'recordingStatus': 'Recording Status',
  'isOnline': 'Connection Status',
  'technician': 'Technician(s)',
  'scorer': 'Scorer(s)',
  'physician': 'Physician(s)',
  // 'rowData': 'Raw data File y/n',
  'totalChannelsCollected': 'Total channels collected',

  'createdAt': 'Test Created Date Time',
  'testCreator': 'Test Creator',
  'startTime': 'Test start Time',
  'endTime': 'Test end Time',
  'signalsStatus': 'Signals Status',
  'reportStatus': 'Algo Status',
  'collectionTechnician': 'Collection technician',
  'testComments': 'Test Comments',
  'end_test': 'End Test',

  "test_logs": "Test Logs",
  'edf':'EDF Link',
  'actions': 'Actions'
}

/**
 * The three physician roles and the labels they are shown under:
 *   Physician        → "Interpreting Physician", signs reports
 *   SharedPhysician  → "External Interpreting Physician", signs reports,
 *                      belongs to the main lab and reaches sub-labs over SSO
 *   PhysicianNoSign  → "Physician", identical to Physician except that it
 *                      cannot approve and sign
 * The keys keep their original spelling because they are the values the API
 * sends; see ROLE_LABEL_MAP for what each is called in the UI.
 */
export enum RolesEnum  {
  Physician = 'PHYSICIAN',
  Scorer = 'SCORER',
  Technician = 'TECHNICIAN',
  Admin = 'ADMIN',
  Viewer = 'VIEWER',
  SuperAdmin = 'SUPER_ADMIN',
  OutsourceScorer = 'OUTSOURCE_SCORER',
  LabManager = 'LAB_MANAGER',
  TechAdmin = 'TECH_ADMIN',
  SharedPhysician = 'SHARED_PHYSICIAN',
  PhysicianNoSign = 'PHYSICIAN_NO_SIGN',
}
export const SHORT_DURATION_THRESHOLD_SECONDS = 4.5*60*60; // 4.5 hours
export const CURRENT_TIME_ZONE_OFFSET = moment().utcOffset()
export const BIG_SCREEN = 1920;
export const VALID_PHONE_PATTERN = /^\+?[1-9]\d{6,14}$/
export const VALID_FAX_PATTERN = /^\+[1-9]\d{6,14}$/
/**
 * Longest address accepted anywhere an email is entered. RFC 5321 caps a full
 * address at 254 characters and the column that stores it holds 255, so a
 * longer value is refused in the form rather than by the database on save.
 */
export const EMAIL_MAX_LENGTH = 254;
/**
 * Local part and domain are runs of word characters joined by single dots or
 * hyphens, ending in a dot plus a suffix of two or more characters.
 *
 * The separators inside the repeated groups are deliberately not optional. An
 * optional separator lets one run of word characters be split across iterations
 * in exponentially many ways, so a long value that cannot match sends the regex
 * engine through all of them and freezes the tab while someone is still typing.
 * Requiring the separator leaves exactly one way to split any input while
 * accepting the same set of addresses.
 */
export const VALID_EMAIL_FORMAT = '^\\w+([.-]\\w+)*@\\w+([.-]\\w+)*\\.\\w{2,}$'
export const VALID_NAME_FORMAT = "^[a-zA-Z0-9\u0590-\u05FF\\s\\-\\.',_()&/]+$";
/**
 * Most tests one opening may create. A patient's lifetime total is not capped —
 * this only limits how many a single New Test(s) / new patient submission opens,
 * so a larger batch is split across separate openings.
 */
export const MAX_TESTS_PER_OPENING = 10;
/** Shown under Number of Tests when the requested count exceeds the cap. */
export const MAX_TESTS_PER_OPENING_ERROR =
  `You can open up to ${MAX_TESTS_PER_OPENING} tests at a time. To open more tests, create another opening.`;
/** : patients below this age default to Pediatric Interpretation. */
export const PEDIATRIC_AGE_LIMIT = 18;
/** Help text behind the Pediatric Interpretation icon on the patient forms. */
export const PEDIATRIC_INTERPRETATION_HINT =
  'Applies pediatric-specific AASM scoring rules and AHI thresholds in the report.';
/**
 * Shown on the patient forms when a patient below PEDIATRIC_AGE_LIMIT is left
 * on the adult ruleset, so the consequence is visible while the checkbox is
 * still in reach.
 */
export const PEDIATRIC_DISABLED_WARNING =
  'Pediatric interpretation is disabled. The study will be scored and interpreted using adult AASM criteria and AHI thresholds';
/** DWA-1618: default snore (microphone) threshold in dB, per population. */
export const SNORE_THRESHOLD_ADULT = 60;
export const SNORE_THRESHOLD_PEDIATRIC = 65;
/**
 * DWA-1620: pediatric studies use a fixed 2s minimum desaturation length,
 * overriding the department's Min Desat Len setting.
 */
export const MIN_DESAT_LEN_PEDIATRIC = 2;
export type TestIndexFieldsType = typeof TestIndexFields;

export type TestIndexFieldsKeysType = keyof TestIndexFieldsType;
export type AllTestIndexFieldsKeysType = TestIndexFieldsKeysType | 'actions';

/**
 * DWA-1542: how the auto-scoring run picks the snore (microphone) threshold.
 * AUTOMATIC sends no `mi_threshold` at all and leaves the choice to the algo.
 */
export enum SnoreDetection {
  AUTOMATIC = 'automatic',
  THRESHOLD = 'threshold',
}

export enum DefaultSpo2Desat {
  AUTOMATIC = 'automatic',
  MANUAL = 'manual',
  RELATIVE = 'relative',
}

/**
 * "Scoring Workflow" — what a department does once the automatic scoring run
 * finishes. MANUAL leaves the test waiting for a scorer to submit it for
 * interpretation; AUTOMATIC submits it straight away, as if "Save and Submit"
 * had been clicked, and notifies the physicians.
 */
export enum ScoringWorkflow {
  MANUAL = 'manual',
  AUTOMATIC = 'automatic',
}

/**
 * "Require Desat for" — which respiratory events are only marked when there is
 * a desaturation next to the event. The value is sent to the algo as the
 * `relative_baseline` param, except for NONE which sends `false`.
 */
export enum RequireDesatFor {
  // No adjustment — the algo runs regularly.
  NONE = 'none',
  // Mark hypopneas only if there is a desat next to the event.
  HYPOPNEA_ONLY = 'hyp_only',
  // Mark apneas only if there is a desat next to the event.
  APNEA_ONLY = 'apnea_only',
  // Mark all respiratory events only if there is a desat next to the event.
  ALL = 'all',
}

/**
 * `include_arousal` — what the respiratory wrapper of the auto-scoring run
 * scores: the respiratory events only, the arousals only, or both. It is a
 * parameter of that wrapper rather than a group of its own, and sending nothing
 * is the same as ALL.
 */
export enum IncludeArousal {
  RESP_ONLY = 'resp_only',
  AROUSAL_ONLY = 'arousal_only',
  ALL = 'all',
}

/**
 * The `include_arousal` value for the "Arousals" and "Respiratory" selections,
 * or `undefined` when neither is selected and the wrapper does not run at all.
 */
export const includeArousalFor = (
  arousal: boolean,
  respiratory: boolean,
): IncludeArousal | undefined => {
  if (arousal && respiratory) {
    return IncludeArousal.ALL;
  }
  if (arousal) {
    return IncludeArousal.AROUSAL_ONLY;
  }
  if (respiratory) {
    return IncludeArousal.RESP_ONLY;
  }
  return undefined;
};

/**
 * The `relative_baseline` value for a "Require Desat for" selection: the mode
 * string itself, or `false` when no adjustment is requested.
 */
export const relativeBaselineFor = (
  requireDesatFor: RequireDesatFor | string | null | undefined,
): RequireDesatFor | string | false =>
  requireDesatFor && requireDesatFor !== RequireDesatFor.NONE
    ? requireDesatFor
    : false;

export const TestIndexFieldsKeys: TestIndexFieldsKeysType[] = Object.keys(TestIndexFields) as TestIndexFieldsKeysType[];

export const AllTestIndexFieldsKeys: AllTestIndexFieldsKeysType[] = [ ...TestIndexFieldsKeys]
export const additionalTestFieldsVisible = [7]

export type TestDownloadableAsset = 'edf' | 'report-pdf'
  export const ALL_RACE_OPTIONS = [
    {viewValue: 'Asian' , value: 'Asian'},
    {viewValue: 'White' , value: 'White'},
    {viewValue: 'Black' , value: 'Black'},
    {viewValue: 'Hispanic' , value: 'Hispanic'},
    {viewValue: 'Other' , value: 'Other'},
  ]

  export const ALL_ALCOHOL_OPTIONS = [
    {viewValue: 'Every day' , value: 'Every day'},
    {viewValue: 'Once a week' , value: 'Once a week'},
    {viewValue: 'Once a month' , value: 'Once a month'},
    {viewValue: 'Rarely' , value: 'Rarely'},
    {viewValue: 'Never' , value: 'Never'},
  ]

  export const ALL_CAFFEINE_OPTIONS = [
    {viewValue: 'Every day' , value: 'Every day'},
    {viewValue: 'Once a week' , value: 'Once a week'},
    {viewValue: 'Once a month' , value: 'Once a month'},
    {viewValue: 'Rarely' , value: 'Rarely'},
    {viewValue: 'Never' , value: 'Never'},
  ]

  export const ALL_EXERCISE_OPTIONS = [
    {viewValue: 'Every day' , value: 'Every day'},
    {viewValue: 'Once a week' , value: 'Once a week'},
    {viewValue: 'Once a month' , value: 'Once a month'},
    {viewValue: 'Rarely' , value: 'Rarely'},
    {viewValue: 'Never' , value: 'Never'},
  ]

  export const ALL_STRESS_LEVEL_OPTIONS = [
    {viewValue: 'High' , value: 'High'},
    {viewValue: 'Medium' , value: 'Medium'},
    {viewValue: 'Low' , value: 'Low'},
  ]


    export const ALL_TEST_ROOM_STATUS_OPTIONS = [
    {viewValue: 'Available'  , value: TestRoomStatusEnum.AVAILABLE},
    {viewValue: 'Occupied' , value: TestRoomStatusEnum.OCCUPIED},
    {viewValue: 'Out Of Service' , value: TestRoomStatusEnum.OUT_OF_SERVICE},
  ]


  export enum GenderEnum   {  MALE = 'MALE',  FEMALE = 'FEMALE' };
  export const ALL_MEDICATION_GROUPS_OPTIONS = [
    { viewValue: 'Analgesics', value: 'Analgesics' },
    { viewValue: 'Anesthetics', value: 'Anesthetics' },
    { viewValue: 'Anti-addiction agents', value: 'Anti-addiction agents' },
    { viewValue: 'Antibacterials', value: 'Antibacterials' },
    { viewValue: 'Anticonvulsants', value: 'Anticonvulsants' },
    { viewValue: 'Antidementia agents', value: 'Antidementia agents' },
    { viewValue: 'Antidepressants', value: 'Antidepressants' },
    { viewValue: 'Antiemetics', value: 'Antiemetics' },
    { viewValue: 'Antifungals', value: 'Antifungals' },
    { viewValue: 'Antigout agents', value: 'Antigout agents' },
    { viewValue: 'Antimigraine agents', value: 'Antimigraine agents' },
    { viewValue: 'Antimycobacterials', value: 'Antimycobacterials' },
    { viewValue: 'Antineoplastics', value: 'Antineoplastics' },
    { viewValue: 'Antiparkinson agents', value: 'Antiparkinson agents' },
    { viewValue: 'Antipsychotics', value: 'Antipsychotics' },
    { viewValue: 'Antispasticity agents', value: 'Antispasticity agents' },
    { viewValue: 'Antivirals', value: 'Antivirals' },
    { viewValue: 'Anxiolytics', value: 'Anxiolytics' },
    { viewValue: 'Bipolar agents', value: 'Bipolar agents' },
    { viewValue: 'Blood glucose regulators', value: 'Blood glucose regulators' },
    { viewValue: 'Blood products', value: 'Blood products' },
    { viewValue: 'Cardiovascular agents', value: 'Cardiovascular agents' },
    { viewValue: 'Central nervous system agents', value: 'Central nervous system agents' },
    { viewValue: 'Dental and oral agents', value: 'Dental and oral agents' },
    { viewValue: 'Dermatological agents', value: 'Dermatological agents' },
    { viewValue: 'Electrolytes, minerals, metals, vitamins', value: 'Electrolytes, minerals, metals, vitamins' },
    { viewValue: 'Gastrointestinal agents', value: 'Gastrointestinal agents' },
    { viewValue: 'Genetic/enzyme/protein disorder agents', value: 'Genetic/enzyme/protein disorder agents' },
    { viewValue: 'Genitourinary agents', value: 'Genitourinary agents' },
    { viewValue: 'Hormonal agents', value: 'Hormonal agents' },
    { viewValue: 'Immunological agents', value: 'Immunological agents' },
    { viewValue: 'Inflammatory bowel disease agents', value: 'Inflammatory bowel disease agents' },
    { viewValue: 'Metabolic bone disease agents', value: 'Metabolic bone disease agents' },
    { viewValue: 'Ophthalmic agents', value: 'Ophthalmic agents' },
    { viewValue: 'Otic agents', value: 'Otic agents' },
    { viewValue: 'Respiratory tract agents', value: 'Respiratory tract agents' },
    { viewValue: 'Skeletal muscle relaxants', value: 'Skeletal muscle relaxants' },
    { viewValue: 'Sleep disorder agents', value: 'Sleep disorder agents' }
];

export const ALL_TEST_LOCATION_OPTIONS = [
  {viewValue: 'Lab' , value: 'Lab'},
  {viewValue: 'Home' , value: 'Home'},
]

export const ALL_TEST_TYPES_OPTIONS = [
  {viewValue: 'PSG' , value: ReportTemplate.PSG},
  {viewValue: 'MSLT' , value: ReportTemplate.MSLT},
  {viewValue: 'HST Type II' , value: ReportTemplate.HST_TYPE_2},
  {viewValue: 'EEG' , value:  ReportTemplate.EEG_SLEEP_WAKE},
  {viewValue: 'Titration' , value: ReportTemplate.TITRATION},
  {viewValue: 'Split Night' , value: ReportTemplate.SPLIT_NIGHT},
]

/**
 * Wording to show for a stored study type, so every surface spells it the same
 * way as the study type picker ("HST type 2" is stored, "HST Type II" is shown).
 * The stored values are the report template values and must not be renamed —
 * only the display wording lives here.
 *
 * Anything without a mapping is passed through unchanged: study types no longer
 * offered in the picker, and aggregate labels such as the charts' "Other"
 * bucket, still need to render as they arrive.
 */
export const testTypeLabel = (value?: string | null): string =>
  ALL_TEST_TYPES_OPTIONS.find((o) => o.value === value)?.viewValue ?? (value ?? '');


export const ALL_DEVICE_TYPE_OPTIONS = [
  {viewValue: 'V1.0' , value: 'V1.0'},
  {viewValue: 'V1.5' , value: 'V1.5'},
  {viewValue: 'V2.0' , value: 'V2.0'},
  {viewValue: 'V3.0' , value: 'V3.0'},
  {viewValue: 'V3.1' , value: 'V3.1'},
]
/**
 * Device statuses exactly as the API stores and returns them. Keep these values
 * byte-identical to the backend enum: the status is compared by exact value on
 * both sides, so a divergent value silently breaks filters, counts and the status
 * dot. They are constants rather than English so the wording can be changed here
 * alone — see DEVICE_STATUS_LABELS.
 */
export enum DeviceStatus {
  New = 'NEW',
  Available = 'AVAILABLE',
  AfterUseAwaitingInspection = 'AFTER_USE',
  Assigned = 'ASSIGNED',
  InUse = 'IN_USE',
  SentForRepair = 'SENT_FOR_REPAIR',
  FaultyDevice = 'FAULTY_DEVICE',
  Charging = 'CHARGING'
}

/**
 * The only place device statuses are spelled in English. Nothing renders
 * `device.status` directly — every surface goes through `deviceStatusLabel`.
 */
export const DEVICE_STATUS_LABELS: Record<DeviceStatus, string> = {
  [DeviceStatus.New]: 'New',
  [DeviceStatus.Available]: 'Available',
  [DeviceStatus.AfterUseAwaitingInspection]: 'After Use',
  [DeviceStatus.Assigned]: 'Assigned',
  [DeviceStatus.InUse]: 'In Use',
  [DeviceStatus.SentForRepair]: 'Sent for repair',
  [DeviceStatus.FaultyDevice]: 'Faulty Device',
  [DeviceStatus.Charging]: 'Charging',
};

/**
 * The English wording the API used to store, keyed by the lower-cased value.
 * Devices are converted to constants in the database, but a row can still arrive
 * with old wording from a cached response or an un-migrated environment, and it
 * must not fall back to rendering the raw string. In Lab equipment statuses are
 * still plain English and resolve through here too.
 */
const LEGACY_DEVICE_STATUS_ALIASES: Record<string, DeviceStatus> = {
  'new': DeviceStatus.New,
  'available': DeviceStatus.Available,
  'available (inspected after use)': DeviceStatus.Available,
  'available after use': DeviceStatus.Available,
  'available (after use)': DeviceStatus.Available,
  'available (inspected)': DeviceStatus.Available,
  'inspected after use': DeviceStatus.Available,
  'after use': DeviceStatus.AfterUseAwaitingInspection,
  'after use (awaiting inspection)': DeviceStatus.AfterUseAwaitingInspection,
  'awaiting inspection': DeviceStatus.AfterUseAwaitingInspection,
  'faulty': DeviceStatus.FaultyDevice,
  'faulty device': DeviceStatus.FaultyDevice,
  'faulty device (under inspection)': DeviceStatus.FaultyDevice,
  'under inspection': DeviceStatus.FaultyDevice,
  'assigned': DeviceStatus.Assigned,
  'in use': DeviceStatus.InUse,
  'inuse': DeviceStatus.InUse,
  'in-use': DeviceStatus.InUse,
  'sent for repair': DeviceStatus.SentForRepair,
  'sent to repair': DeviceStatus.SentForRepair,
  'in repair': DeviceStatus.SentForRepair,
  'repair': DeviceStatus.SentForRepair,
  'charging': DeviceStatus.Charging,
};

/** Resolves any spelling of a status to the canonical stored value. */
export const normalizeDeviceStatus = (status?: string | null): DeviceStatus | undefined => {
  if (!status) return undefined;

  const key = status.trim().toLowerCase();
  const canonical = Object.values(DeviceStatus).find((v) => v.toLowerCase() === key);

  return canonical ?? LEGACY_DEVICE_STATUS_ALIASES[key];
};

/** Wording to show for a status coming off the API. */
export const deviceStatusLabel = (status?: string | null): string => {
  const canonical = normalizeDeviceStatus(status);

  return canonical ? DEVICE_STATUS_LABELS[canonical] : (status ?? '');
};


/**
 * Statuses in which a device is committed to a test (assigned before start, or
 * in use once started). Devices in these states cannot be assigned to another
 * test and cannot be deleted.
 */
export const DEVICE_OCCUPIED_STATUSES: DeviceStatus[] = [
  DeviceStatus.Assigned,
  DeviceStatus.InUse,
];

export const isDeviceOccupied = (status: string): boolean => {
  const canonical = normalizeDeviceStatus(status);

  return !!canonical && DEVICE_OCCUPIED_STATUSES.includes(canonical);
};

/**
 * Statuses offered in the status pickers and filters, in display order. Faulty
 * Device is deliberately absent: it is set by the inspection flow, never picked
 * by hand.
 */
const SELECTABLE_DEVICE_STATUSES: DeviceStatus[] = [
  DeviceStatus.New,
  DeviceStatus.Available,
  DeviceStatus.AfterUseAwaitingInspection,
  DeviceStatus.Assigned,
  DeviceStatus.InUse,
  DeviceStatus.SentForRepair,
  DeviceStatus.Charging,
];

export const ALL_DEVICE_STATUS_OPTIONS = SELECTABLE_DEVICE_STATUSES.map((value) => ({
  viewValue: DEVICE_STATUS_LABELS[value],
  value,
}));

export enum DeviceUsageType {
  IN_LAB = 'IN_LAB',
  HOME = 'HOME',
}


export const DEVICE_USAGE_TYPE_OPTIONS = [
  { value: DeviceUsageType.IN_LAB, viewValue: 'In Lab' },
  { value: DeviceUsageType.HOME, viewValue: 'HST' },
];

export const ALL_DEVICE_SIZE_OPTIONS = [
  {viewValue: 'S' , value: 'S'},
  {viewValue: 'M' , value: 'M'},
  {viewValue: 'L' , value: 'L'},
  {viewValue: 'Child' , value: 'Child'},
]

/**
 * In Lab additional equipment (cameras / tablets). Kept separate from the
 * device enums: equipment is not recordable and never goes through the
 * device inspection flow, so it has its own status list. These are still stored
 * as English in `lab_equipment.status`, unlike device statuses.
 */
export enum EquipmentType {
  Camera = 'CAMERA',
  Tablet = 'TABLET',
}

export const EQUIPMENT_TYPE_OPTIONS = [
  { viewValue: 'Camera', value: EquipmentType.Camera },
  { viewValue: 'Tablet', value: EquipmentType.Tablet },
];

export enum EquipmentStatus {
  New = 'New',
  Available = 'Available',
  Assigned = 'Assigned',
  InUse = 'In use',
  SentForRepair = 'Sent for repair',
}

export const EQUIPMENT_STATUS_OPTIONS = [
  { viewValue: 'New', value: EquipmentStatus.New },
  { viewValue: 'Available', value: EquipmentStatus.Available },
  { viewValue: 'In Use', value: EquipmentStatus.InUse },
  { viewValue: 'Assigned', value: EquipmentStatus.Assigned },
  { viewValue: 'Sent to Repair', value: EquipmentStatus.SentForRepair },
];

/**
 * The Devices screen shares one status filter across all three tables, but
 * devices are filtered by constant and equipment by its English status. Translate
 * so filtering by "Available" doesn't empty the equipment table; device-only
 * statuses are passed through (and correctly match nothing).
 */
const DEVICE_TO_EQUIPMENT_STATUS: Record<string, EquipmentStatus> = {
  [DeviceStatus.New]: EquipmentStatus.New,
  [DeviceStatus.Available]: EquipmentStatus.Available,
  [DeviceStatus.Assigned]: EquipmentStatus.Assigned,
  [DeviceStatus.InUse]: EquipmentStatus.InUse,
  [DeviceStatus.SentForRepair]: EquipmentStatus.SentForRepair,
};

export const equipmentStatusFromDeviceStatus = (status: string): string =>
  DEVICE_TO_EQUIPMENT_STATUS[status] ?? status;

export const equipmentTypeLabel = (type: string): string =>
  EQUIPMENT_TYPE_OPTIONS.find((o) => o.value === type)?.viewValue ?? (type ?? '');
export const BOOLEAN_CHOOSE = [
  { viewValue: 'Yes', value: true },
  { viewValue: 'No', value: false },
]

export const DEPARTMENT_DEFAULT_SPO2_DESAT_OPTIONS = [
  { viewValue: 'Automatic', value: DefaultSpo2Desat.AUTOMATIC },
  { viewValue: 'Manual', value: DefaultSpo2Desat.MANUAL },
  { viewValue: 'Relative', value: DefaultSpo2Desat.RELATIVE },
]

export const SCORING_WORKFLOW_OPTIONS = [
  { viewValue: 'Manual', value: ScoringWorkflow.MANUAL },
  { viewValue: 'Automatic', value: ScoringWorkflow.AUTOMATIC },
]

// Options for "Require Desat for" — maps to the algo `relative_baseline` param.
export const REQUIRE_DESAT_OPTIONS = [
  { viewValue: 'None', value: RequireDesatFor.NONE },
  { viewValue: 'Hypopnea Only', value: RequireDesatFor.HYPOPNEA_ONLY },
  { viewValue: 'Apnea Only', value: RequireDesatFor.APNEA_ONLY },
  { viewValue: 'All', value: RequireDesatFor.ALL },
]


export const ALL_DEPARTMENTS_ID = '-1';
